import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface PaymentTerm {
  id: string
  organizationId: string
  name: string
  description?: string
  /** Rows of { percentage, days, description }; older terms may still arrive as an object, so read it with normalizePaymentSchedule. */
  schedule: Array<{ percentage: number; days: number; description?: string }> | Record<string, any>
  isDefault: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface PaymentTermFilters {
  isActive?: boolean
  search?: string
}

export function usePaymentTerms(filters?: PaymentTermFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['paymentTerms', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      if (filters?.search) params.set('search', filters.search)
      const res = await api.get<any>(`/master-data/payment-terms?${params}`)
      return (res?.data || []) as PaymentTerm[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function usePaymentTermMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['paymentTerms', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<PaymentTerm, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/payment-terms', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<PaymentTerm> }) =>
      api.patch<any>(`/master-data/payment-terms/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/payment-terms/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/payment-terms/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setDefault = useMutation({
    mutationFn: (id: string) =>
      api.post<any>(`/master-data/payment-terms/${id}/set-default`, {}, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove, setDefault }
}